// The two small marks a LIVE game carries beyond its score: the win-probability strip and the latest callout.
// P6-330b (GN-3b). Drawn on the Tonight cards and at the foot of the game view's hero, from `funModel.js`'s answers
// (this file decides nothing: an answer of `null` is never passed in, so there is no empty strip and no empty line).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE NUMBERS ARE TEXT, AND THE BAR IS THE PICTURE OF THEM
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's strip is three labels over a six-pixel bar: `MIA 62%`, `Win probability`, `38% TOR`, and a bar the
// away team's share wide. The two percentages are real text (a screen reader reads them), so the bar is
// `aria-hidden`: it repeats what the words say. Its colours are the chart palette (`--hoops-away-line` and
// `--hoops-home-line`, `colors.js::chartStyle`), and a side with no usable primary is drawn in a neutral.

/**
 * `strip` is `funModel.js::stripFrom`'s answer (`{ away, home }`, two whole percentages that make a hundred) and
 * `away` and `home` are the sides' tricodes. `testId` names it for the tests.
 *
 * `age` is ESPN's age, `funModel.js::espnAgeLine`'s answer (`ESPN · 2m`), and is `null` while ESPN's stamp is fresh, which
 * is nearly always: then nothing is drawn for it and the strip is exactly what it was. P6-334 (GN-7). It is a line UNDER the
 * bar, not a word in the title row: that row already holds two tricodes, their percentages and the title at 320px.
 */
export function WinStrip({ strip, away, home, testId, age = null }) {
  return (
    <div className="d2d-hoops__wp" data-testid={testId} data-away={strip.away} data-home={strip.home}>
      <div className="d2d-hoops__wplabels d2d-micro">
        <span data-side="away">
          {away} {strip.away}%
        </span>
        <span className="d2d-hoops__wptitle">Win probability</span>
        <span data-side="home">
          {strip.home}% {home}
        </span>
      </div>
      <div className="d2d-hoops__wpbar" aria-hidden="true">
        <span className="d2d-hoops__wpfill d2d-hoops__wpfill--away" style={{ width: `calc(${strip.away}% - 2px)` }} />
        <span className="d2d-hoops__wpfill d2d-hoops__wpfill--home" />
      </div>
      {age === null ? null : (
        <div className="d2d-hoops__wpage d2d-micro" data-testid={`${testId}-age`}>
          {age}
        </div>
      )}
    </div>
  )
}

/**
 * `callout` is `funModel.js::liveCallout`'s answer: the words (`MIA 8–0 run`), when it was (`Q1 8:54`) and which side
 * it is (the tick's colour, if any). `large` is the hero's (the design's 16px against a card's 15px).
 */
export function Callout({ callout, testId, large = false }) {
  return (
    <div
      className={large ? 'd2d-hoops__callout d2d-hoops__callout--large' : 'd2d-hoops__callout'}
      data-testid={testId}
      data-kind={callout.kind}
      data-side={callout.side ?? 'none'}
    >
      <span className="d2d-hoops__calloutline">{callout.line}</span>
      <span className="d2d-hoops__calloutwhen d2d-micro">{callout.when}</span>
    </div>
  )
}
